import {Spot, Zone} from "../types";

export type RuleReference = {
    kind: "spot" | "zone";
    id: string;
    name: string;
};

const spotDisplayName = (spot: Spot, index: number) =>
    spot.name && spot.name.trim().length > 0 ? spot.name : `Spot ${index + 1}`;

const zoneDisplayName = (zone: Zone) =>
    (zone.label && zone.label.trim().length > 0 && zone.label)
    || (zone.name && zone.name.trim().length > 0 && zone.name)
    || zone.id;

const ruleMatchesSpotKey = (rule: {type?: string; spotKey?: string} | undefined, spotKey: string) =>
    !!rule && rule.type === "Spot" && !!rule.spotKey && rule.spotKey === spotKey;

const ruleMatchesZoneId = (rule: {type?: string; zoneKey?: string} | undefined, zoneId: string) =>
    !!rule && rule.type === "Ambient" && !!rule.zoneKey && rule.zoneKey === zoneId;

const someRuleMatches = (
    rules: Array<{type?: string; spotKey?: string; zoneKey?: string}> | undefined,
    predicate: (rule: {type?: string; spotKey?: string; zoneKey?: string}) => boolean
) => Array.isArray(rules) && rules.some(predicate);

// Fail-closed sentinel: callers should treat null as "references exist" (block the delete).
export type ReferenceScanResult = RuleReference[] | null;

export const findReferencesToSpot = (
    spotKey: string | undefined,
    allSpots: Spot[],
    ambientZones: Zone[]
): ReferenceScanResult => {
    if (!spotKey) {
        return [];
    }
    try {
        const refs: RuleReference[] = [];
        allSpots.forEach((spot, index) => {
            if (spot.key === spotKey) {
                return;
            }
            if (someRuleMatches(spot.triggerRules?.rules, (rule) => ruleMatchesSpotKey(rule, spotKey))) {
                refs.push({kind: "spot", id: spot.id, name: spotDisplayName(spot, index)});
            }
        });
        ambientZones.forEach((zone) => {
            if (someRuleMatches(zone.triggerRules?.rules, (rule) => ruleMatchesSpotKey(rule, spotKey))) {
                refs.push({kind: "zone", id: zone.id, name: zoneDisplayName(zone)});
            }
        });
        return refs;
    } catch (error) {
        console.error("findReferencesToSpot failed", error);
        return null;
    }
};

export const findReferencesToZone = (
    zoneId: string | undefined,
    allSpots: Spot[],
    ambientZones: Zone[]
): ReferenceScanResult => {
    if (!zoneId) {
        return [];
    }
    try {
        const refs: RuleReference[] = [];
        allSpots.forEach((spot, index) => {
            if (someRuleMatches(spot.triggerRules?.rules, (rule) => ruleMatchesZoneId(rule, zoneId))) {
                refs.push({kind: "spot", id: spot.id, name: spotDisplayName(spot, index)});
            }
        });
        ambientZones.forEach((zone) => {
            if (zone.id === zoneId) {
                return;
            }
            if (someRuleMatches(zone.triggerRules?.rules, (rule) => ruleMatchesZoneId(rule, zoneId))) {
                refs.push({kind: "zone", id: zone.id, name: zoneDisplayName(zone)});
            }
        });
        return refs;
    } catch (error) {
        console.error("findReferencesToZone failed", error);
        return null;
    }
};

type RuleLike = {type?: string; spotKey?: string; zoneKey?: string};

export const isDanglingRule = (
    rule: RuleLike | undefined,
    allSpots: Spot[],
    ambientZones: Zone[]
): boolean => {
    if (!rule) {
        return false;
    }
    if (rule.type === "Spot") {
        if (!rule.spotKey) {
            return false;
        }
        return !allSpots.some((spot) => spot.key === rule.spotKey);
    }
    if (rule.type === "Ambient") {
        if (!rule.zoneKey) {
            return false;
        }
        return !ambientZones.some((zone) => zone.id === rule.zoneKey);
    }
    return false;
};

export const hasAnyDanglingRule = (
    rules: RuleLike[] | undefined,
    allSpots: Spot[],
    ambientZones: Zone[]
): boolean => {
    if (!Array.isArray(rules) || rules.length === 0) {
        return false;
    }
    return rules.some((rule) => isDanglingRule(rule, allSpots, ambientZones));
};

export const formatReferenceList = (refs: RuleReference[]): string => {
    if (refs.length === 0) {
        return "";
    }
    return refs.map((ref) => `• ${ref.kind === "spot" ? "Spot" : "Ambient"}: ${ref.name}`).join("\n");
};
